export type SelectOptionsType = { title: string; value: string; label: string };
export type SelectOptionsHtmlType = {
  title: string;
  value: string;
  label: any;
};

// Data Transformers
type DtoSelectOptionType = {
  id?: number | string;
  name?: string;
  isActive?: boolean;
};
type AsSelectOptionsType = {
  data: DtoSelectOptionType[];
  doFilter?: boolean;
  doSort?: boolean;
  labelColumn?: string;
  valueColumn?: string;
};
export const asSelectOptions = ({
  data,
  doFilter = false,
  doSort = true,
  labelColumn,
  valueColumn,
}: AsSelectOptionsType): SelectOptionsType[] => {
  const selectOptions: SelectOptionsType[] = [];
  if (doFilter) {
    data
      .filter((element) => {
        return element.isActive;
      })
      .forEach((element) => {
        selectOptions.push({
          title: "", //suppress hover popout
          value: valueColumn ? (element as any)[valueColumn] : `${element.id}`,
          label: labelColumn
            ? (element as any)[labelColumn]
            : element.name || "-",
        });
      });
  } else {
    data.forEach((element) => {
      selectOptions.push({
        title: "",
        value: valueColumn ? (element as any)[valueColumn] : `${element.id}`,
        label: labelColumn
          ? (element as any)[labelColumn]
          : element.name || "-",
      });
    });
  }
  if (doSort) {
    selectOptions.sort(selectOptionsComparator);
  }
  return selectOptions;
};

export const getValueFromLabel = (
  label: string,
  options?: SelectOptionsType[]
): string => {
  if (!options) {
    return "-1";
  }
  const returnValue = options.find(
    (option) => `${option.label}` === `${label}`
  );
  return returnValue?.value || "-1";
};

export const getValuesFromLabels = (
  labels: string[],
  options?: SelectOptionsType[]
): string[] => {
  if (!options) {
    return ["-1"];
  }
  const returnValues = options
    .filter((option) => labels.includes(option.label))
    .map((option) => option.value);
  return returnValues.length > 0 ? returnValues : [];
};

export const getLabelFromValue = (
  value: string | number,
  options?: SelectOptionsType[]
): string => {
  if (!options) {
    return "-1";
  }
  const returnValue = options.find(
    (option) => `${option.value}` === `${value}`
  );
  return returnValue?.label || "-1";
};

export const getLabelsAsValues = (options?: SelectOptionsType[]) => {
  return options?.map((option) => ({ value: option.label }));
};

export const selectOptionsComparator = (
  a: SelectOptionsType,
  b: SelectOptionsType
) => {
  const aa = (a["label"] || "").toString().toLowerCase();
  const bb = (b["label"] || "").toString().toLowerCase();
  if (aa < bb) {
    return -1;
  }
  if (aa > bb) {
    return 1;
  }
  return 0;
};

export const selectOptionsHtmlComparator = (
  a: SelectOptionsHtmlType,
  b: SelectOptionsHtmlType
) => {
  if (a?.label.props.children < b?.label.props.children) return -1;
  if (a?.label.props.children < b?.label.props.children) return 1;
  return 0;
};

/**
 * Creates a regular expression based on the provided filter text.
 *
 * This function sanitizes the input string by escaping special regex characters
 * and replacing asterisks (`*`) with `.*` to allow wildcard matching. The resulting
 * regular expression matches strings that start with the sanitized input, ignoring case.
 *
 * @param filterText - The input string to be converted into a regular expression.
 * @returns A `RegExp` object that matches strings starting with the sanitized input.
 */
export const createRegex = (filterText: string) => {
  const sanitizedInput = (filterText || "")
    .replace(/[-/\\^$+?.()|[\]{}]/g, "\\$&")
    .replace(/\*/g, ".*");
  return new RegExp(`^${sanitizedInput}`, "i");
};
